// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import { isRtlLang } from "@/utils";
import Cell from "./Cell";
import { isCategoryRowBlank, isRowDirty, type TyCategoryDraft, type TyRow } from "./model";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    rows: TyRow<TyCategoryDraft>[];
    invalidKeys: Set<string>;
    onChange: (key: string, patch: Partial<TyCategoryDraft>) => void;
    onRevert: (key: string) => void;
    onRemove: (key: string) => void;
    /** Enter on the last row appends one and lands in the same column. */
    onAddRow: (column: string) => void;
};

// --- Logic --------------------------------------------------------------------------------------

/** Order is not a column — the rows are shown in their saved order and re-arranged from the bar. */
export default function CategoriesGrid({ rows, invalidKeys, onChange, onRevert, onRemove, onAddRow }: Props) {
    const t = useTranslation();
    const { languages } = useStoreLanguages();

    /** A row still holding nothing is abandoned on Escape rather than reverted to its empty self. */
    const escapeHandler = (row: TyRow<TyCategoryDraft>) => (isCategoryRowBlank(row) ? () => onRemove(row.key) : undefined);

    return (
        <table className="editor-table">
            <thead>
                <tr>
                    <th className="editor-th editor-th--index">#</th>
                    {languages.map((lang) => (
                        <th key={lang} className="editor-th editor-th--name">
                            {`${t.menuEditor.nameColumn} · ${t.languages[lang]}`}
                        </th>
                    ))}
                    <th className="editor-th editor-th--count">{t.menuEditor.products}</th>
                    <th className="editor-th editor-th--tools" aria-label={t.menuEditor.rowTools} />
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => {
                    const dirty = isRowDirty(row);
                    const invalid = invalidKeys.has(row.key);
                    return (
                        <tr
                            key={row.key}
                            data-row-key={row.key}
                            className={`editor-tr${dirty ? " editor-tr--dirty" : ""}${invalid ? " editor-tr--invalid" : ""}`}
                        >
                            <td className="editor-td editor-td--index">
                                <span className="editor-index">{index + 1}</span>
                                {!row._id && <span className="editor-new-badge">{t.menuEditor.newBadge}</span>}
                            </td>
                            {languages.map((lang) => (
                                <td key={lang} className="editor-td">
                                    <Cell
                                        value={row.draft.name[lang] ?? ""}
                                        onChange={(value) => onChange(row.key, { name: { ...row.draft.name, [lang]: value } })}
                                        onRevert={() => onRevert(row.key)}
                                        onEscapeEmpty={escapeHandler(row)}
                                        onEnterPastEnd={() => onAddRow(`name_${lang}`)}
                                        column={`name_${lang}`}
                                        rowIndex={index}
                                        dirty={dirty && row.draft.name[lang] !== row.saved?.name[lang]}
                                        dir={isRtlLang(lang) ? "rtl" : "ltr"}
                                        placeholder={t.languages[lang]}
                                    />
                                </td>
                            ))}
                            <td className="editor-td editor-td--count">{row.draft.productsCount}</td>
                            <td className="editor-td editor-td--tools">
                                {dirty && (
                                    <button
                                        type="button"
                                        className="editor-row-btn"
                                        onClick={() => (row._id ? onRevert(row.key) : onRemove(row.key))}
                                        aria-label={row._id ? t.menuEditor.revertRow : t.menuEditor.removeRow}
                                        title={row._id ? t.menuEditor.revertRow : t.menuEditor.removeRow}
                                    >
                                        <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
